// AmbientScene — per-theme ambient background behind the chat window (mirror of CyberRain, which
// stays the Cyberpunk effect). Pure decoration (pointer-events:none), sits at z-index 0 inside
// .chat__main; re-renders on theme change via useTheme; frozen by prefers-reduced-motion in CSS.
//   deep → starfield · terra → falling leaves · aurora → drifting aurora blobs
//   arcade → rising neon pixels · yuletide → snow · spooky → bats + fog
//   bttf → DeLorean + tire-fire trails + stars + embers + lightning
import { useTheme } from "../hooks/useTheme.js";

// Deterministic pseudo-random (stable across re-renders, no Math.random) — seed → [a,b).
const r = (i, a, b) => { const x = Math.sin((i + 1) * 12.9898) * 43758.5453; return a + (x - Math.floor(x)) * (b - a); };

function Stars() {
  return (
    <div className="amb amb--deep" aria-hidden>
      {Array.from({ length: 70 }).map((_, i) => (
        <span key={i} className="amb-star" style={{
          left: `${r(i, 0, 100).toFixed(2)}%`,
          top: `${r(i + 31, 0, 100).toFixed(2)}%`,
          width: `${r(i + 7, 1, 2.8).toFixed(2)}px`,
          height: `${r(i + 7, 1, 2.8).toFixed(2)}px`,
          animationDuration: `${r(i + 13, 2.4, 6).toFixed(2)}s`,
          animationDelay: `${(-r(i + 5, 0, 6)).toFixed(2)}s`,
        }} />
      ))}
      {Array.from({ length: 3 }).map((_, i) => (
        <span key={`sh${i}`} className="amb-shoot" style={{
          left: `${r(i + 90, 10, 80).toFixed(2)}%`,
          top: `${r(i + 60, 5, 45).toFixed(2)}%`,
          animationDuration: `${r(i + 3, 6, 11).toFixed(2)}s`,
          animationDelay: `${(-r(i + 2, 0, 9)).toFixed(2)}s`,
        }} />
      ))}
    </div>
  );
}

function Leaves() {
  const glyphs = ["🍂", "🍁", "🍂", "🌿", "🍁"];
  return (
    <div className="amb amb--terra" aria-hidden>
      {Array.from({ length: 16 }).map((_, i) => (
        <span key={i} className="amb-leaf" style={{
          left: `${r(i, 0, 100).toFixed(2)}%`,
          fontSize: `${r(i + 11, 14, 26).toFixed(1)}px`,
          animationDuration: `${r(i + 4, 9, 18).toFixed(2)}s`,
          animationDelay: `${(-r(i + 6, 0, 14)).toFixed(2)}s`,
        }}>{glyphs[i % glyphs.length]}</span>
      ))}
    </div>
  );
}

function Aurora() {
  return (
    <div className="amb amb--aurora" aria-hidden>
      {Array.from({ length: 4 }).map((_, i) => (
        <span key={i} className={`amb-blob amb-blob--${i}`} style={{
          animationDuration: `${r(i + 2, 16, 28).toFixed(2)}s`,
          animationDelay: `${(-r(i + 1, 0, 12)).toFixed(2)}s`,
        }} />
      ))}
    </div>
  );
}

function Pixels() {
  return (
    <div className="amb amb--arcade" aria-hidden>
      {Array.from({ length: 22 }).map((_, i) => (
        <span key={i} className="amb-pixel" style={{
          left: `${r(i, 0, 100).toFixed(2)}%`,
          width: `${r(i + 9, 4, 9).toFixed(1)}px`,
          height: `${r(i + 9, 4, 9).toFixed(1)}px`,
          animationDuration: `${r(i + 5, 6, 13).toFixed(2)}s`,
          animationDelay: `${(-r(i + 3, 0, 10)).toFixed(2)}s`,
          "--hue": i % 3, // 0 pink · 1 cyan · 2 purple (see CSS)
        }} />
      ))}
    </div>
  );
}

function Snow() {
  return (
    <div className="amb amb--yuletide" aria-hidden>
      {Array.from({ length: 44 }).map((_, i) => (
        <span key={i} className="amb-flake" style={{
          left: `${r(i, 0, 100).toFixed(2)}%`,
          fontSize: `${r(i + 8, 8, 18).toFixed(1)}px`,
          opacity: r(i + 2, 0.4, 0.95).toFixed(2),
          animationDuration: `${r(i + 4, 7, 15).toFixed(2)}s`,
          animationDelay: `${(-r(i + 6, 0, 12)).toFixed(2)}s`,
        }}>❄</span>
      ))}
    </div>
  );
}

function Bats() {
  // Fewer bats on small screens — the spooky hero cards are already animation-heavy on mobile.
  const count = typeof window !== "undefined" && window.innerWidth < 560 ? 4 : 9;
  return (
    <div className="amb amb--spooky" aria-hidden>
      <span className="amb-fog" />
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className="amb-bat" style={{
          top: `${r(i, 4, 70).toFixed(2)}%`,
          fontSize: `${r(i + 12, 14, 28).toFixed(1)}px`,
          animationDuration: `${r(i + 5, 9, 18).toFixed(2)}s`,
          animationDelay: `${(-r(i + 7, 0, 15)).toFixed(2)}s`,
        }}>🦇</span>
      ))}
    </div>
  );
}

// Stylised side-view DMC DeLorean (time-machine trim: Mr. Fusion + reactor stack on the rear
// deck, glowing flux sill, exhaust flames). Pure inline SVG — no image assets.
function Delorean() {
  return (
    <svg className="amb-delorean" viewBox="0 0 120 46" aria-hidden>
      <defs>
        <linearGradient id="bttf-steel" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#dde7f3" />
          <stop offset=".55" stopColor="#9fb2c9" />
          <stop offset="1" stopColor="#6d7f96" />
        </linearGradient>
      </defs>
      <polygon points="0,23 10,20 10,26" fill="#ff9a1f" opacity=".9" />
      <polygon points="2,28 12,25 12,29" fill="#ffd23f" opacity=".8" />
      <circle cx="17" cy="7" r="3.4" fill="#c7d4e4" />
      <rect x="11" y="9" width="13" height="6" rx="1.5" fill="#8fa3bd" />
      <polygon points="8,32 8,20 11,15 38,15 45,9 66,9 89,19 114,25 117,32" fill="url(#bttf-steel)" />
      <polygon points="41,14.5 46,10 64,10 76,15.5 62,18.5 44,18.5" fill="#0c1531" />
      <line x1="57" y1="10" x2="62" y2="28" stroke="#5f7186" strokeWidth="1" />
      <line x1="14" y1="17" x2="34" y2="17" stroke="#5f7186" strokeWidth="1.2" />
      <line x1="14" y1="20" x2="34" y2="20" stroke="#5f7186" strokeWidth="1.2" />
      <rect x="12" y="29" width="100" height="2.2" rx="1.1" fill="#7fd4ff" opacity=".95" />
      <rect x="9" y="16" width="3" height="14" rx="1.5" fill="#7fd4ff" opacity=".9" />
      <circle cx="31" cy="34" r="7" fill="#0a0f1e" />
      <circle cx="31" cy="34" r="2.6" fill="#cfd9e8" />
      <circle cx="97" cy="34" r="7" fill="#0a0f1e" />
      <circle cx="97" cy="34" r="2.6" fill="#cfd9e8" />
    </svg>
  );
}

function TimeTravel() {
  return (
    <div className="amb amb--bttf" aria-hidden>
      <span className="amb-flash" />
      {Array.from({ length: 30 }).map((_, i) => (
        <span key={`st${i}`} className="amb-star" style={{
          left: `${r(i + 70, 0, 100).toFixed(2)}%`,
          top: `${r(i + 21, 0, 55).toFixed(2)}%`,
          width: `${r(i + 7, 1, 2.4).toFixed(2)}px`,
          height: `${r(i + 7, 1, 2.4).toFixed(2)}px`,
          animationDuration: `${r(i + 13, 2.4, 6).toFixed(2)}s`,
          animationDelay: `${(-r(i + 5, 0, 6)).toFixed(2)}s`,
        }} />
      ))}
      {Array.from({ length: 2 }).map((_, i) => (
        <span key={`dr${i}`} className="amb-drive" style={{
          top: `${(52 + i * 20).toFixed(0)}%`,
          animationDuration: `${r(i + 4, 14, 22).toFixed(2)}s`,
          animationDelay: `${(-r(i + 8, 0, 12)).toFixed(2)}s`,
        }}>
          <span className="amb-trail" />
          <Delorean />
        </span>
      ))}
      {Array.from({ length: 12 }).map((_, i) => (
        <span key={i} className="amb-ember" style={{
          left: `${r(i, 0, 100).toFixed(2)}%`,
          width: `${r(i + 9, 3, 6).toFixed(1)}px`,
          height: `${r(i + 9, 3, 6).toFixed(1)}px`,
          animationDuration: `${r(i + 5, 7, 14).toFixed(2)}s`,
          animationDelay: `${(-r(i + 3, 0, 11)).toFixed(2)}s`,
        }} />
      ))}
      {Array.from({ length: 3 }).map((_, i) => (
        <span key={`bo${i}`} className="amb-bolt" style={{
          left: `${r(i + 40, 8, 88).toFixed(2)}%`,
          top: `${r(i + 55, 5, 40).toFixed(2)}%`,
          fontSize: `${r(i + 17, 18, 32).toFixed(1)}px`,
          animationDuration: `${r(i + 6, 6, 10).toFixed(2)}s`,
          animationDelay: `${(-r(i + 9, 0, 8)).toFixed(2)}s`,
        }}>⚡</span>
      ))}
    </div>
  );
}

export default function AmbientScene() {
  const { theme } = useTheme();
  switch (theme) {
    case "deep": return <Stars />;
    case "terra": return <Leaves />;
    case "aurora": return <Aurora />;
    case "arcade": return <Pixels />;
    case "yuletide": return <Snow />;
    case "spooky": return <Bats />;
    case "bttf": return <TimeTravel />;
    default: return null; // default (clean) + cyberpunk (CyberRain handles it)
  }
}
